import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from "../../layout/LayoutHeader";
import { postDataAPI, getDataAPI } from "../../utils/fetchData";
import axios from 'axios';
import { BASE_URL } from "../../utils/axios";
import usageTracker from "../../utils/usageTracker";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";

const BulkImportCatalog = () => {
    const [step, setStep] = useState(3); // Start directly at Spotify discovery step
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const { modalConfig, showModal, showConfirm, closeModal } = useModal();
    const fileInputRef = useRef(null);
    const progressIntervalRef = useRef(null); // Track the progress interval
    const [uploadedData, setUploadedData] = useState([]);
    const [curEditData, setEditData] = useState(null);
    const [curEditKey, setKey] = useState(-1);
    const [isProcessing, setProcessing] = useState(false);
    const [spotifyUrl, setSpotifyUrl] = useState('');
    const [artistName, setArtistName] = useState('');
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [discoveredData, setDiscoveredData] = useState(null);
    const [artistList, setArtistList] = useState([]);
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [selectedArtist, setSelectedArtist] = useState(null);

    // Batch save modal state
    const [showBatchSaveModal, setShowBatchSaveModal] = useState(false);
    const [batchSaveProgress, setBatchSaveProgress] = useState({
        currentBatch: 0,
        totalBatches: 0,
        processedItems: 0,
        totalItems: 0,
        isProcessing: false
    });

    // Discovery progress tracking
    const [discoveryProgress, setDiscoveryProgress] = useState({
        percentage: 0,
        isActive: false,
        message: 'Ready to discover...'
    });

    // Scroll-based help text visibility
    const [showHelpText, setShowHelpText] = useState(true);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    // DEBUG: Track uploadedData changes
    useEffect(() => {
        console.log(`📊 FRONTEND DEBUG - uploadedData state changed:`, uploadedData.length, 'entries');
        if (uploadedData.length > 0) {
            console.log(`📊 FRONTEND DEBUG - Sample entries in state:`, uploadedData.slice(0, 3));
        }
    }, [uploadedData]);

    // Add scroll listener to hide/show help text
    useEffect(() => {
        const handleScroll = (e) => {
            const scrollTop = e.target.scrollTop;
            setShowHelpText(scrollTop < 50); // Hide when scrolled more than 50px
        };

        const scrollContainer = document.querySelector('.bulk-import-scroll-container');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const handleSpotifyImport = () => {
        setStep(3); // Step for Spotify import
    };

    const validateSpotifyLink = (url) => {
        return url.includes('spotify.com/artist/') && url.includes('https://');
    };

    const searchArtists = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setArtistList([]);
            setShowArtistDropdown(false);
            return;
        }

        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await postDataAPI("user/searchArtistsForBulkImport", { 
                artistName: searchTerm
            }, token);

            console.log('Artist search response:', response.data);

            if (response.data?.status === 200) {
                const artists = response.data?.data?.response || [];
                setArtistList(artists);
                setShowArtistDropdown(artists.length > 0);
            } else {
                setArtistList([]);
                setShowArtistDropdown(false);
            }
        } catch (error) {
            console.error("Error searching artists:", error);
            setArtistList([]);
            setShowArtistDropdown(false);
        }
    };

    const handleArtistNameChange = (e) => {
        const value = e.target.value;
        setArtistName(value);
        setSelectedArtist(null);
        
        // Search for artists when typing
        if (value.length >= 2) {
            searchArtists(value);
        } else {
            setArtistList([]);
            setShowArtistDropdown(false);
        }
    };

    const selectArtist = (artist) => {
        setSelectedArtist(artist);
        setArtistName(artist.name);
        setSpotifyUrl(artist.spotifyUrl);
        setArtistList([]);
        setShowArtistDropdown(false);
    };

    const discoverCatalog = async () => {
        if (!spotifyUrl && !artistName) {
            showModal('Input Required', 'Please enter either a Spotify artist URL or artist name', 'warning');
            return;
        }

        setIsDiscovering(true);
        
        // Start simple percentage progress
        setDiscoveryProgress({
            percentage: 0,
            isActive: true,
            message: 'Starting discovery...'
        });

        // Clear any existing interval
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }

        // Simple percentage counter that gradually increases
        progressIntervalRef.current = setInterval(() => {
            setDiscoveryProgress(prev => {
                if (!prev.isActive) return prev;
                
                // Gradually increase percentage with varying speed
                let increment = 0;
                
                if (prev.percentage < 10) {
                    // Slow start (1-3%)
                    increment = Math.random() > 0.7 ? Math.floor(Math.random() * 3) + 1 : 0;
                } else if (prev.percentage < 30) {
                    // Steady early progress (1-4%)
                    increment = Math.random() > 0.5 ? Math.floor(Math.random() * 4) + 1 : 0;
                } else if (prev.percentage < 70) {
                    // Faster middle progress (1-5%)
                    increment = Math.random() > 0.4 ? Math.floor(Math.random() * 5) + 1 : 0;
                } else if (prev.percentage < 85) {
                    // Slower near end (1-2%)
                    increment = Math.random() > 0.7 ? Math.floor(Math.random() * 2) + 1 : 0;
                } else {
                    // Very slow final processing (0-1%)
                    increment = Math.random() > 0.9 ? 1 : 0;
                }
                
                const newPercentage = Math.min(prev.percentage + increment, 92); // Stop at 92% until completion
                const newMessage = `Discovering catalog... ${newPercentage}%`;
                
                return {
                    ...prev,
                    percentage: newPercentage,
                    message: newMessage
                };
            });
        }, 800); // Update every 800ms

        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            console.log('Starting catalog discovery...');
            
            const requestData = {
                spotifyUrl: spotifyUrl || undefined,
                artistName: artistName || undefined
            };
            
            // Step 1: Start the background process with a reasonable timeout
            let response;
            try {
                response = await postDataAPI("user/discoverCatalogFromArtist", requestData, token, {
                    timeout: 300000 // 5 minutes - reasonable for most cases
                });
            } catch (error) {
                // If it times out, try with background processing approach
                if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                    console.log('Request timed out, attempting background processing...');
                    
                    // Update progress to show we're switching to background mode
                    setDiscoveryProgress({
                        percentage: 75,
                        isActive: true,
                        message: "Large catalog detected - processing in background..."
                    });
                    
                    // Try with a shorter timeout to initiate background processing
                    try {
                        response = await postDataAPI("user/discoverCatalogFromArtist", requestData, token, {
                            timeout: 120000 // 2 minutes
                        });
                    } catch (backgroundError) {
                        // If it still times out, show appropriate message
                        throw new Error('BACKGROUND_PROCESSING_TIMEOUT');
                    }
                } else {
                    throw error;
                }
            }

            // Stop progress simulation
            clearInterval(progressIntervalRef.current);

            console.log('Discover catalog response:', response.data);

            if (response.data?.success) {
                const catalogData = response.data?.data || [];
                
                // Complete progress to 100%
                setDiscoveryProgress({
                    percentage: 100,
                    isActive: false,
                    message: "Discovery completed successfully! 100%"
                });
                
                setDiscoveredData(response.data);
                // Sort by release date chronologically (most recent first)
                const sortedCatalogData = catalogData.sort((a, b) => {
                    const dateA = new Date(a.release_date || a.releaseDate || '1900-01-01');
                    const dateB = new Date(b.release_date || b.releaseDate || '1900-01-01');
                    return dateB - dateA; // Descending order (newest first)
                });
                setUploadedData(sortedCatalogData);
                setStep(2); // Go to review step
                
            } else {
                showModal('Discovery Failed', response.data?.message || "Couldn't discover catalog data. Please try again.", 'error');
            }
            
        } catch (error) {
            // Stop progress simulation on error
            clearInterval(progressIntervalRef.current);
            setDiscoveryProgress({
                percentage: 0,
                isActive: false,
                message: 'Discovery failed'
            });
            
            console.error("Error discovering catalog:", error);
            
            // Enhanced error handling for different scenarios
            if (error.message === 'BACKGROUND_PROCESSING_TIMEOUT') {
                showModal(
                    'Processing Timeout',
                    `Large catalog processing is taking longer than expected in production. 

This typically happens with artists who have 200+ tracks. The system is designed to handle this, but production servers have stricter timeout limits.

Suggestions:
1. Try again in a few minutes - the background process may still be running
2. For very large catalogs (500+ songs), consider processing in smaller batches
3. Contact support if you consistently encounter this with smaller catalogs

The discovery process may still be running on our servers. Please wait 2-3 minutes before trying again.`,
                    'warning'
                );
            } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                showModal(
                    'Request Timeout',
                    `Request timed out. This typically happens with very large catalogs in production environments.

For large catalogs (100+ tracks), our servers need more time to process all the data. 

What you can do:
• Wait 2-3 minutes and try again (the process may still be running)
• For artists with 300+ songs, consider contacting support for assistance
• The timeout is usually due to the large amount of data being processed

Production servers have stricter limits than local development, which is why this works locally but times out in production.`,
                    'warning'
                );
            } else if (error.response?.status === 504) {
                showModal(
                    'Gateway Timeout',
                    `Gateway timeout occurred. This is a production infrastructure limitation when processing very large catalogs.

The backend processing may still be continuing. Please:
• Wait 3-5 minutes before trying again
• For catalogs with 200+ tracks, this timeout is common in production
• Contact support if you need assistance with very large artist catalogs

This works locally because local servers don't have the same gateway timeout restrictions as production infrastructure.`,
                    'warning'
                );
            } else if (error.response?.status >= 500) {
                showModal(
                    'Server Error',
                    `Server error occurred. This might be due to high server load or a temporary issue.

Please try again in a few minutes. If the issue persists with smaller catalogs (under 100 tracks), please contact support.`,
                    'error'
                );
            } else {
                showModal('Discovery Error', "Error discovering catalog data. Please check your internet connection and try again.", 'error');
            }
        } finally {
            setIsDiscovering(false);
            setDiscoveryProgress(prev => ({ ...prev, isActive: false }));
            // Ensure interval is cleared
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        }
    };

    const saveEdit = () => {
        setStep(2);
        let tempData = uploadedData;
        tempData[curEditKey] = curEditData;
        // Sort by release date chronologically (most recent first)
        const sortedTempData = tempData.sort((a, b) => {
            const dateA = new Date(a.release_date || a.releaseDate || '1900-01-01');
            const dateB = new Date(b.release_date || b.releaseDate || '1900-01-01');
            return dateB - dateA; // Descending order (newest first)
        });
        setUploadedData([...sortedTempData]);
        setKey(-1);
    };

    /**
     * Check for duplicate songs based on song name, artist name, and ISRC
     * @param {Array} newTracks - Array of new tracks to check
     * @param {Array} existingCatalog - Array of existing catalog tracks
     * @returns {Object} - {filteredTracks, duplicates, duplicateCount}
     */
    const filterDuplicates = (newTracks, existingCatalog) => {
        console.log('🔍 Checking for duplicates in', newTracks.length, 'new tracks against', existingCatalog.length, 'existing tracks');
        
        const duplicates = [];
        const filteredTracks = [];
        
        newTracks.forEach((newTrack, index) => {
            const isDuplicate = existingCatalog.some(existingTrack => {
                // Normalize strings for comparison (lowercase, trim whitespace)
                const normalize = (str) => str ? str.toLowerCase().trim() : '';
                
                const newSongName = normalize(newTrack.song_name || newTrack.songName || newTrack.title);
                const newArtistName = normalize(newTrack.artist_name || newTrack.artistName || newTrack.artist);
                const newISRC = normalize(newTrack.isrc || newTrack.ISRC);
                
                const existingSongName = normalize(existingTrack.song_name || existingTrack.songName || existingTrack.title);
                const existingArtistName = normalize(existingTrack.artist_name || existingTrack.artistName || existingTrack.artist);
                const existingISRC = normalize(existingTrack.isrc || existingTrack.ISRC);
                
                // Check for duplicate based on multiple criteria
                const sameSongAndArtist = newSongName && newArtistName && 
                                        newSongName === existingSongName && 
                                        newArtistName === existingArtistName;
                
                const sameISRC = newISRC && existingISRC && newISRC === existingISRC;
                
                // Consider it a duplicate if:
                // 1. Same ISRC (most reliable identifier)
                // 2. Same song name AND artist name
                return sameISRC || sameSongAndArtist;
            });
            
            if (isDuplicate) {
                duplicates.push({
                    ...newTrack,
                    originalIndex: index
                });
                console.log(`🚫 Duplicate found: "${newTrack.song_name || newTrack.title}" by "${newTrack.artist_name || newTrack.artist}"`);
            } else {
                filteredTracks.push(newTrack);
            }
        });
        
        console.log(`✅ Duplicate check complete: ${filteredTracks.length} unique tracks, ${duplicates.length} duplicates found`);
        
        return {
            filteredTracks,
            duplicates,
            duplicateCount: duplicates.length
        };
    };

    const handleSave = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            
            console.log(`handleSave called with ${uploadedData.length} tracks`);
            
            // Step 1: Get existing catalog to check for duplicates
            console.log('📚 Fetching existing catalog to check for duplicates...');
            let existingCatalog = [];
            try {
                const catalogResponse = await getDataAPI('user/getCatalog', token);
                if (catalogResponse?.data?.status === 200 && catalogResponse?.data?.data) {
                    existingCatalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
                    console.log('📚 Found', existingCatalog?.length || 0, 'existing tracks in catalog');
                }
            } catch (catalogError) {
                console.warn('⚠️ Could not fetch existing catalog, proceeding without duplicate check:', catalogError);
            }
            
            // Step 2: Filter out duplicates
            const { filteredTracks, duplicates, duplicateCount } = filterDuplicates(uploadedData, existingCatalog);
            
            // Step 3: Show duplicate information to user
            if (duplicateCount > 0) {
                const duplicateNames = duplicates.slice(0, 3).map(d => `"${d.song_name || d.title}" by ${d.artist_name || d.artist}`).join(', ');
                const message = `Found ${duplicateCount} duplicate song${duplicateCount > 1 ? 's' : ''} that won't be imported:\n\n${duplicateNames}${duplicateCount > 3 ? `\n...and ${duplicateCount - 3} more` : ''}\n\nContinue with importing ${filteredTracks.length} unique tracks?`;
                
                return new Promise((resolve) => {
                    showConfirm(
                        'Duplicates Found',
                        message,
                        () => {
                            console.log('✅ User confirmed import despite duplicates');
                            resolve(true);
                            // Continue with the save process
                            continueSaveAfterDuplicateCheck(filteredTracks, duplicateCount);
                        },
                        'warning',
                        'Continue',
                        'Cancel'
                    );
                });
            }
            
            // If no duplicates, continue directly
            await continueSaveAfterDuplicateCheck(filteredTracks, 0);
        } catch (error) {
            console.error('🚨 Error in handleSave:', error);
            showModal('Save Error', 'Error saving catalog. Please try again.', 'error');
        }
    };
    
    // Helper function to continue save process after duplicate check
    const continueSaveAfterDuplicateCheck = async (filteredTracks, duplicateCount) => {
        try {
            
            // Step 4: Check if we have any tracks left to save
            if (filteredTracks.length === 0) {
                showModal('No New Tracks', 'No new tracks to import. All tracks are duplicates of existing catalog items.', 'info');
                return;
            }
            
            console.log(`💾 Proceeding to save ${filteredTracks.length} unique tracks (${duplicateCount} duplicates removed)`);
            
            // Step 5: Save the filtered tracks
            // For large catalogs (>20 items), use batch processing
            if (filteredTracks.length > 20) {
                console.log(`Large catalog detected: ${filteredTracks.length} tracks. Using batch save.`);
                showModal('Large Catalog', `Large catalog detected (${filteredTracks.length} tracks). Using batch processing...`, 'info');
                // Update uploadedData with filtered tracks for batch processing
                setUploadedData(filteredTracks);
                return await handleBatchSave();
            }
            
            console.log(`Small catalog: ${filteredTracks.length} tracks. Using single save.`);
            const response = await postDataAPI(
                'user/saveCatalog',
                { data: filteredTracks },
                token
            );
            
            if (response.data.status === 200) {
                const successMessage = duplicateCount > 0 
                    ? `Catalog saved successfully! ${filteredTracks.length} tracks imported, ${duplicateCount} duplicates skipped.`
                    : 'Catalog saved successfully!';
                
                // Track catalog usage for bulk import
                try {
                    const actionType = filteredTracks.length > 10 ? 'bulk_import_operation' : 'catalog_entry_basic';
                    await usageTracker.track(actionType, { 
                        song_count: filteredTracks.length,
                        source: 'bulk_import' 
                    });
                } catch (trackingError) {
                    console.error('Usage tracking failed:', trackingError);
                }
                
                // Trigger reminder system update after successful save
                console.log('📅 Bulk catalog saved - triggering reminder system check...');
                
                // Dispatch storage event to trigger reminder updates
                localStorage.setItem('catalog_updated', Date.now().toString());
                
                // Also dispatch custom event for same-tab updates
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));
                
                // Force reminder system to check for updates
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
                }, 500);
                
                showModal('Success', successMessage, 'success');
                setTimeout(() => {
                    navigate('/catalog/view-all');
                }, 1500);
            } else {
                console.log('Save response not successful:', response.data);
                showModal('Save Error', 'Error saving catalog. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error saving catalog:', error);
            if (error.response?.data?.error === 'request entity too large') {
                console.log('Payload too large error caught. Switching to batch save.');
                showModal('Catalog Too Large', 'Catalog is too large for single save. Using batch processing...', 'warning');
                await handleBatchSave();
            } else {
                showModal('Save Error', 'Error saving catalog. Please try again.', 'error');
            }
        }
    };

    const handleBatchSave = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const batchSize = 50;
            const totalBatches = Math.ceil(uploadedData.length / batchSize);
            
            // Initialize batch save progress
            setBatchSaveProgress({
                currentBatch: 0,
                totalBatches,
                processedItems: 0,
                totalItems: uploadedData.length,
                isProcessing: true
            });
            setShowBatchSaveModal(true);
            
            let allSavedItems = [];
            
            // Process each batch
            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const startIndex = batchIndex * batchSize;
                const endIndex = Math.min(startIndex + batchSize, uploadedData.length);
                const currentBatch = uploadedData.slice(startIndex, endIndex);
                
                console.log(`Processing batch ${batchIndex + 1}/${totalBatches}: items ${startIndex + 1} to ${endIndex}`);
                
                // Update progress
                setBatchSaveProgress(prev => ({
                    ...prev,
                    currentBatch: batchIndex + 1,
                    processedItems: startIndex
                }));
                
                const response = await postDataAPI(
                    'user/saveCatalogBatch',
                    { 
                        data: currentBatch,
                        batchIndex,
                        batchSize
                    },
                    token
                );
                
                if (response.data.status === 200) {
                    allSavedItems = [...allSavedItems, ...response.data.data.data];
                    console.log(`Batch ${batchIndex + 1} completed: ${response.data.data.itemsSaved} items saved in this batch`);
                    
                    // Update progress with actual processed items
                    setBatchSaveProgress(prev => ({
                        ...prev,
                        processedItems: (batchIndex + 1) * batchSize > uploadedData.length 
                            ? uploadedData.length 
                            : (batchIndex + 1) * batchSize
                    }));
                } else {
                    throw new Error(`Batch ${batchIndex + 1} failed: ${response.data.message}`);
                }
                
                // Small delay between batches to avoid overwhelming the server
                if (batchIndex < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
            
            // Final progress update
            setBatchSaveProgress(prev => ({
                ...prev,
                processedItems: uploadedData.length,
                isProcessing: false
            }));
            
            // Success notification
            setTimeout(() => {
                setShowBatchSaveModal(false);
                
                // Trigger reminder system update after successful batch save
                console.log('📅 Batch catalog saved - triggering reminder system check...');
                
                // Dispatch storage event to trigger reminder updates
                localStorage.setItem('catalog_updated', Date.now().toString());
                
                // Also dispatch custom event for same-tab updates
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));
                
                // Force reminder system to check for updates
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
                }, 500);
                
                showModal('Success', `All ${uploadedData.length} catalog items saved successfully!`, 'success');
                setTimeout(() => {
                    navigate('/catalog/view-all');
                }, 1500);
            }, 1000);
            
        } catch (error) {
            console.error('Error in batch save:', error);
            setBatchSaveProgress(prev => ({ ...prev, isProcessing: false }));
            showModal('Batch Save Error', 'Error during batch save. Please try again.', 'error');
        }
    };

    const formatOptions = [
        { name: 'Spotify', description: 'Discover catalog from Spotify', disabled: false },
        { name: 'Excel', description: 'Excel format support coming soon', disabled: true },
        { name: 'JSON', description: 'JSON format support coming soon', disabled: true }
    ];

    // Batch save progress modal
    const renderBatchSaveModal = () => {
        if (!showBatchSaveModal) return null;

        const progressPercentage = batchSaveProgress.totalItems > 0 
            ? Math.round((batchSaveProgress.processedItems / batchSaveProgress.totalItems) * 100)
            : 0;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50">
                <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center justify-between mb-6 relative">
                        <h3 className="text-white text-[18px] font-bold text-left flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#17A2B8] mr-3"></div>
                            Saving Large Catalog
                        </h3>
                    </div>
                    
                    <div className="mb-4">
                        <p className="text-[rgba(255,255,255,0.7)] text-[11px] mb-3">
                            Processing {batchSaveProgress.totalItems} tracks in batches for optimal performance.
                        </p>
                        
                        <div className="flex justify-between text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                            <span>Progress</span>
                            <span>{progressPercentage}%</span>
                        </div>
                        <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-2">
                            <div 
                                className="bg-[#17A2B8] h-2 rounded-full transition-all duration-500"
                                style={{ width: `${progressPercentage}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
                        <div className="text-center">
                            <div className="text-[18px] font-bold text-[#17A2B8] mb-1">
                                {batchSaveProgress.processedItems} / {batchSaveProgress.totalItems}
                            </div>
                            <div className="text-[10px] text-[rgba(255,255,255,0.7)]">
                                Tracks Saved
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-center">
                            <div className="font-semibold text-white text-[11px] mb-1">Current Batch</div>
                            <div className="text-[#17A2B8]">{batchSaveProgress.currentBatch} / {batchSaveProgress.totalBatches}</div>
                        </div>
                        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-center">
                            <div className="font-semibold text-white text-[11px] mb-1">Status</div>
                            <div className={batchSaveProgress.isProcessing ? "text-yellow-400" : "text-green-400"}>
                                {batchSaveProgress.isProcessing ? 'Processing...' : 'Complete'}
                            </div>
                        </div>
                    </div>

                    {batchSaveProgress.isProcessing && (
                        <div className="text-center">
                            <div className="text-[10px] text-[rgba(255,255,255,0.7)]">
                                Please wait while we save your catalog...
                            </div>
                        </div>
                    )}
                    
                    {!batchSaveProgress.isProcessing && (
                        <div className="text-center">
                            <div className="text-[11px] text-green-400 font-semibold flex items-center justify-center">
                                <span className="mr-2">✅</span>
                                All tracks saved successfully!
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Bulk Import</span>
                        <div className="flex items-center gap-3">
                            {/* Save button for review and edit steps */}
                            {(step === 2 || step === 4) && (
                                <button
                                    onClick={step === 4 ? saveEdit : handleSave}
                                    className="bg-[#17A2B8] text-white text-[10px] px-3 py-1 rounded hover:bg-[rgba(23,162,184,0.9)] transition-all"
                                >
                                    {step === 4 ? 'Save Changes' : `Save All (${uploadedData.length})`}
                                </button>
                            )}
                            
                            {/* Navigation Buttons - Only show when not in review or edit mode */}
                            {step !== 2 && step !== 4 && (
                                <>
                                    <span 
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                        onClick={() => navigate('/catalog/view-all')}
                                    >
                                        View
                                    </span>
                                    
                                    <span 
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                        onClick={() => navigate('/catalog/manual-add')}
                                    >
                                        Manual Add
                                    </span>
                                </>
                            )}
                            
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => {
                                    if (step === 4) {
                                        // If in edit mode, go back to review step
                                        setStep(2);
                                        setKey(-1);
                                    } else if (step === 3) {
                                        // If in discovery step, go back to previous page
                                        navigate(-1);
                                    } else if (step === 2) {
                                        // If in review step, go back to format selection
                                        setStep(0);
                                    } else {
                                        // Default: go back to previous page
                                        navigate(-1);
                                    }
                                }}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    <div className="bulk-import-scroll-container w-full px-4 flex-1 overflow-y-auto">
                        {step === 0 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Choose your import format</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Select the format of your catalog data file</p>
                                </div>

                                <div className="space-y-4">
                                    {formatOptions.map((format, index) => (
                                        <div 
                                            key={index}
                                            className={`bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 transition-all ${
                                                format.disabled 
                                                    ? 'opacity-50 cursor-not-allowed' 
                                                    : 'hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] cursor-pointer'
                                            }`}
                                            onClick={format.disabled ? null : (format.name === 'Spotify' ? handleSpotifyImport : null)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-white text-[12px] font-semibold mb-1">
                                                        {format.name}
                                                    </h3>
                                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                        {format.description}
                                                    </p>
                                                </div>
                                                {!format.disabled && (
                                                    <div className="w-3 h-3 rounded-full border-2 border-[#17A2B8]" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}



                        {step === 2 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Review imported data</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Verify your catalog data before saving ({uploadedData.length} tracks found)</p>
                                    {discoveredData && (
                                        <div className="mt-2 space-y-1">
                                            <p className="text-[10px] text-[rgba(23,162,184,0.8)]">
                                                Source: {discoveredData.source || 'Spotify Discovery'}
                                            </p>
                                            <p className="text-[10px] text-[rgba(34,197,94,0.8)]">
                                                ✓ Complete catalog import ({uploadedData.length} tracks)
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4 mb-6">
                                    {console.log(`📊 FRONTEND DEBUG - About to render ${uploadedData.length} items`)}
                                    {uploadedData.map((item, index) => (
                                        <div 
                                            key={index}
                                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    {/* Track Header */}
                                                    <div className="flex items-center gap-3 mb-3">
                                                        {item.album_artwork && (
                                                            <img 
                                                                src={item.album_artwork} 
                                                                alt={item.song_title}
                                                                className="w-12 h-12 rounded object-cover"
                                                            />
                                                        )}
                                                        <div>
                                                            <h3 className="text-white text-[12px] font-semibold mb-1">
                                                                {item.song_title || 'Unknown Track'}
                                                            </h3>
                                                            <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-1">
                                                                {item.artist_name || 'Unknown Artist'}
                                                            </p>
                                                            {item.album_name && (
                                                                <p className="text-[rgba(255,255,255,0.5)] text-[9px]">
                                                                    {item.album_name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Key Data Fields */}
                                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                                        {item.release_date && (
                                                            <div className="text-[10px]">
                                                                <span className="text-[rgba(255,255,255,0.5)]">Release: </span>
                                                                <span className="text-white">{item.release_date}</span>
                                                            </div>
                                                        )}
                                                        {item.duration && (
                                                            <div className="text-[10px]">
                                                                <span className="text-[rgba(255,255,255,0.5)]">Duration: </span>
                                                                <span className="text-white">{item.duration}</span>
                                                            </div>
                                                        )}
                                                        {item.isrc && (
                                                            <div className="text-[10px]">
                                                                <span className="text-[rgba(255,255,255,0.5)]">ISRC: </span>
                                                                <span className="text-white">{item.isrc}</span>
                                                            </div>
                                                        )}
                                                        {item.bpm && (
                                                            <div className="text-[10px]">
                                                                <span className="text-[rgba(255,255,255,0.5)]">BPM: </span>
                                                                <span className="text-white">{item.bpm}</span>
                                                            </div>
                                                        )}
                                                        {item.record_label && (
                                                            <div className="text-[10px] col-span-2">
                                                                <span className="text-[rgba(255,255,255,0.5)]">Label: </span>
                                                                <span className="text-white">{item.record_label}</span>
                                                            </div>
                                                        )}
                                                        {item.distributor && (
                                                            <div className="text-[10px] col-span-2">
                                                                <span className="text-[rgba(255,255,255,0.5)]">Distributor: </span>
                                                                <span className="text-white">{item.distributor}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Tags */}
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.genre && (
                                                            <span className="bg-[rgba(23,162,184,0.2)] text-[rgba(23,162,184,1)] px-2 py-1 rounded text-[9px]">
                                                                {item.genre}
                                                            </span>
                                                        )}
                                                        {item.key_signature && (
                                                            <span className="bg-[rgba(255,255,255,0.1)] text-white px-2 py-1 rounded text-[9px]">
                                                                {item.key_signature}
                                                            </span>
                                                        )}
                                                        {item.songwriter && (
                                                            <span className="bg-[rgba(255,255,255,0.1)] text-white px-2 py-1 rounded text-[9px]">
                                                                Writer: {item.songwriter.split(',')[0]}
                                                            </span>
                                                        )}
                                                        {item.producer && (
                                                            <span className="bg-[rgba(255,255,255,0.1)] text-white px-2 py-1 rounded text-[9px]">
                                                                Producer: {item.producer.split(',')[0]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setEditData(item);
                                                        setKey(index);
                                                        setStep(4);
                                                    }}
                                                    className="text-[#17A2B8] text-[10px] hover:opacity-70 ml-3 mt-1"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                    <button
                                        onClick={handleSave}
                                        className="w-full bg-[#17A2B8] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(23,162,184,0.9)] transition-all"
                                    >
                                        Save All Catalog Items ({uploadedData.length})
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">Enter your artist name or Spotify artist URL to import your music catalog</p>
                                </div>

                                <div className="space-y-4">
                                    {/* Artist Name Input with Dropdown */}
                                    <div className="relative bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                            Artist Name
                                        </label>
                                        <input
                                            type="text"
                                            value={artistName}
                                            onChange={handleArtistNameChange}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter artist name to search"
                                        />
                                        <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                                            We'll search for the artist and import their catalog
                                        </p>
                                        
                                        {/* Artist Dropdown */}
                                        {showArtistDropdown && artistList.length > 0 && (
                                            <div className="absolute z-10 top-full left-0 right-0 mt-2 overflow-hidden rounded-lg shadow-lg">
                                                <div className="max-h-[200px] overflow-y-auto bg-[#1A1A1A] border border-[rgba(255,255,255,0.1)]">
                                                    {artistList.map((artist, index) => (
                                                        <div
                                                            key={`${artist.spotifyUrl}-${index}`}
                                                            className="flex items-center gap-3 p-3 hover:bg-[rgba(23,162,184,0.1)] cursor-pointer transition-colors border-b border-[rgba(255,255,255,0.05)] last:border-b-0"
                                                            onClick={() => selectArtist(artist)}
                                                        >
                                                            {artist.image && (
                                                                <img 
                                                                    src={artist.image} 
                                                                    alt={artist.name}
                                                                    className="w-8 h-8 rounded-full object-cover"
                                                                />
                                                            )}
                                                            <div className="flex-1">
                                                                <div className="text-white text-[12px] font-semibold">
                                                                    {artist.name}
                                                                </div>
                                                                <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                                    {artist.followers ? `${artist.followers.toLocaleString()} followers` : 'Spotify Artist'}
                                                                </div>
                                                            </div>
                                                            {artist.verified && (
                                                                <div className="text-[#17A2B8] text-[8px] bg-[rgba(23,162,184,0.2)] px-2 py-1 rounded">
                                                                    Popular
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {showArtistDropdown && artistList.length === 0 && artistName.length >= 2 && (
                                            <div className="absolute z-10 top-full left-0 right-0 mt-2 overflow-hidden rounded-lg shadow-lg">
                                                <div className="bg-[#1A1A1A] border border-[rgba(255,255,255,0.1)] p-3">
                                                    <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                        No artists found for "{artistName}"
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* OR Divider */}
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 h-px bg-[rgba(255,255,255,0.1)]"></div>
                                        <span className="text-[rgba(255,255,255,0.5)] text-[10px]">OR</span>
                                        <div className="flex-1 h-px bg-[rgba(255,255,255,0.1)]"></div>
                                    </div>

                                    {/* Spotify URL Input */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                            Spotify Artist URL
                                        </label>
                                        <input
                        type="text"
                                            value={spotifyUrl}
                                            onChange={(e) => setSpotifyUrl(e.target.value)}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="https://open.spotify.com/artist/..."
                                        />
                                        <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                                            Paste your Spotify artist profile URL for best results
                                        </p>
                                    </div>

                                    {/* Discover Button */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <button
                                            onClick={() => discoverCatalog()}
                                            disabled={isDiscovering || (!spotifyUrl && !artistName)}
                                            className="w-full bg-[#17A2B8] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(23,162,184,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isDiscovering ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    <span>{discoveryProgress.message}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                    Discover Catalog
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 4 && curEditData && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Edit catalog details</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Modify the catalog information as needed</p>
                                </div>

                                {/* Basic Fields */}
                                <div className="space-y-4 mb-6">
                                    <div className="text-[14px] text-white font-normal mb-3" style={{ fontWeight: 300 }}>Basic Information</div>
                                    
                                    {/* Song Title */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Song Title</label>
                                        <input
                                            type="text"
                                            value={curEditData.song_title || ''}
                                            onChange={(e) => setEditData({...curEditData, song_title: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter song title"
                                        />
                                    </div>

                                    {/* Artist Name */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>Artist Name</label>
                                        <input
                                            type="text"
                                            value={curEditData.artist_name || ''}
                                            onChange={(e) => setEditData({...curEditData, artist_name: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter artist name"
                                        />
                                    </div>

                                    {/* Album Name */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Album Name</label>
                                        <input
                                            type="text"
                                            value={curEditData.album_name || ''}
                                            onChange={(e) => setEditData({...curEditData, album_name: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter album name"
                                        />
                                    </div>

                                    {/* Genre */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Genre</label>
                                        <input
                                            type="text"
                                            value={curEditData.genre || ''}
                                            onChange={(e) => setEditData({...curEditData, genre: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter genre"
                                        />
                                    </div>

                                    {/* Release Date */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Release Date</label>
                                        <input
                                            type="date"
                                            value={curEditData.release_date || ''}
                                            onChange={(e) => setEditData({...curEditData, release_date: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                        />
                                    </div>

                                    {/* Duration */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Duration</label>
                                        <input
                                            type="text"
                                            value={curEditData.duration || ''}
                                            onChange={(e) => setEditData({...curEditData, duration: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter duration (e.g., 3:45)"
                                        />
                                    </div>

                                    {/* ISRC */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">ISRC</label>
                                        <input
                                            type="text"
                                            value={curEditData.isrc || ''}
                                            onChange={(e) => setEditData({...curEditData, isrc: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter ISRC code"
                                        />
                                    </div>

                                    {/* UPC */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">UPC</label>
                                        <input
                                            type="text"
                                            value={curEditData.upc || ''}
                                            onChange={(e) => setEditData({...curEditData, upc: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter UPC code"
                                        />
                                    </div>

                                    {/* Record Label */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Record Label</label>
                                        <input
                                            type="text"
                                            value={curEditData.record_label || ''}
                                            onChange={(e) => setEditData({...curEditData, record_label: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter record label"
                                        />
                                    </div>

                                    {/* Distributor */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Distributor</label>
                                        <input
                                            type="text"
                                            value={curEditData.distributor || ''}
                                            onChange={(e) => setEditData({...curEditData, distributor: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter distributor"
                                        />
                                    </div>

                                    {/* Songwriter */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Songwriter</label>
                                        <input
                                            type="text"
                                            value={curEditData.songwriter || ''}
                                            onChange={(e) => setEditData({...curEditData, songwriter: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter songwriter(s)"
                                        />
                                    </div>

                                    {/* Producer */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Producer</label>
                                        <input
                                            type="text"
                                            value={curEditData.producer || ''}
                                            onChange={(e) => setEditData({...curEditData, producer: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            placeholder="Enter producer(s)"
                                        />
                                    </div>

                                    {/* Notes */}
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                        <label className="block text-white text-[12px] font-semibold mb-2">Notes</label>
                                        <textarea
                                            value={curEditData.notes || ''}
                                            onChange={(e) => setEditData({...curEditData, notes: e.target.value})}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                                            placeholder="Enter additional notes"
                                        />
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                    <button
                                        onClick={saveEdit}
                                        className="w-full bg-[#17A2B8] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(23,162,184,0.9)] transition-all"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Help Text - Hidden on scroll */}
                        {showHelpText && (
                            <div className="mt-6 mb-4">
                                <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                    {step === 0 && (
                                        <>
                                            <p>Import multiple catalog items at once from Spotify.</p>
                                            <p>Discover your complete catalog with detailed metadata automatically.</p>
                                        </>
                                    )}
                                    {step === 2 && (
                                        <>
                                            <p>Review each catalog entry before saving to your account.</p>
                                            <p>Click Edit to modify any track details if needed.</p>
                                        </>
                                    )}
                                    {step === 3 && (
                                        <></>
                                    )}
                                    {step === 4 && (
                                        <>
                                            <p>Update any fields that need correction.</p>
                                            <p>Changes will be applied when you save.</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {renderBatchSaveModal()}
            
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default BulkImportCatalog;